import { SignInIcon } from '@egw/assets'
import { type AuthsVsPasskeyErrorTypes, type PasskeyErrorTypes, useAuth, type PasskeyError } from '@egw/auth'
import { Alert, AlertType, Button, ButtonVariant, Input } from '@egw/components'
import { type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { sendEvent, setDeviceContext } from '@egw/events'
import { useFlags } from '@egw/feature-flags'
import { Trans, useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { riskService } from '@egw/risk-service'
import { ArrowLongRightIcon, ArrowRightIcon } from '@heroicons/react/24/solid'
import cn from 'classnames'
import { type FC, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router'
import { v4 as uuidv4 } from 'uuid'

const log = new Logger('Auth')

interface FormData {
  email: string
}

const getDefaultFormData = (): FormData => {
  const savedEmail = localStorage.getItem('lastSignInEmail')
  return {
    email: savedEmail ?? '',
  }
}

export interface SignInFormProps {
  onError?: (_value: boolean) => void
  onHelp: () => void
  sessionTimeout?: boolean
  recoveryExpired?: boolean
}
export const SignInForm: FC<SignInFormProps> = ({ onError, onHelp, recoveryExpired, sessionTimeout = false }) => {
  const { flags } = useFlags<FeatureFlags>()

  return flags.rolloutSignInPageRedesign ? (
    <SignInFormCompRedesign onHelp={onHelp} recoveryExpired={recoveryExpired} sessionTimeout={sessionTimeout} />
  ) : (
    <SignInFormComp
      onError={onError}
      onHelp={onHelp}
      recoveryExpired={recoveryExpired}
      sessionTimeout={sessionTimeout}
    />
  )
}

export const SignInFormComp: FC<SignInFormProps> = ({ onError, onHelp, recoveryExpired, sessionTimeout = false }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isFormLoading, setIsFormLoading] = useState<boolean>(false)
  const [isError, setIsError] = useState(false)
  const [isFormError, setIsFormError] = useState(false)
  const { t } = useTranslation('features-auth')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectUrl = searchParams.get('redirect')

  const { signInWithPasskey } = useAuth()
  const otpChallengeHanlder = (): void => {
    sendEvent('eg_signIn_singleFactor', {
      eventSource: 'egWealth',
      category: 'signIn',
      subCategory: 'signle-factor',
    })
    void navigate(WEALTH_ROUTES.SIGN_IN_CHALLENGE.path)
  }

  const handleAuthStart = async (email?: string): Promise<void> => {
    const passkeySignInRequestUID = uuidv4()
    setDeviceContext({
      passkeySignInRequestUID,
    })
    sendEvent('user_signIn_click', {
      eventSource: 'egWealth',
      category: 'signIn',
      subCategory: 'intent',
      emailProvided: false,
    })
    try {
      setIsFormError(false)
      setIsError(false)
      onError?.(false)
      setIsLoading(true)
      await riskService.updateFlow('core')
      await signInWithPasskey(email, otpChallengeHanlder).then(() => {
        log.debug('handleAuthStart', `navigating to ${redirectUrl ?? WEALTH_ROUTES.OVERVIEW.path}`)
        void navigate(redirectUrl ?? WEALTH_ROUTES.OVERVIEW.path)
      })
    } catch (err) {
      setIsError(true)
      onError?.(true)
      log.error('Sign In with Passkey Error: ', err)
    } finally {
      setIsLoading(false)
    }
  }

  const defaultFormData = useMemo(() => getDefaultFormData(), [])

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors },
  } = useForm({
    defaultValues: defaultFormData,
  })

  const onSubmit = handleSubmit(async (formData: unknown) => {
    sendEvent('user_signIn_click', {
      eventSource: 'egWealth',
      category: 'signIn',
      subCategory: 'intent',
      emailProvided: true,
    })
    try {
      setIsFormLoading(true)
      setIsFormError(false)
      onError?.(false)
      setIsError(false)
      const { email } = formData as FormData
      await riskService.updateFlow('core')
      await signInWithPasskey(email).then(() => {
        log.debug('onSubmit', `navigating to ${redirectUrl ?? WEALTH_ROUTES.OVERVIEW.path}`)
        void navigate(redirectUrl ?? WEALTH_ROUTES.OVERVIEW.path)
      })
    } catch (err) {
      setIsFormError(true)
      log.error('Sign In with Passkey Error: ', err)
      onError?.(true)
    } finally {
      setIsFormLoading(false)
    }
  })

  return (
    <>
      <h1 className="pt-24 mb-8 text-3xl font-light text-center">{t('sign_in.title')}</h1>
      <div className="flex flex-col items-center">
        <div className="flex flex-col p-6 bg-white mx-6 shadow-xl gap-5 rounded h-fit sm:max-w-[342px] md:w-[442px] relative z-10">
          <h2 className="font-light text-2xl text-center">{t('sign_in_form.title')}</h2>

          {!isError && sessionTimeout ? (
            <Alert
              alertType={AlertType.Info}
              testId="session-timeout-alert"
              description={t('sign_in_form.session_timeout_message')}
            />
          ) : null}
          {isError ? <Alert alertType={AlertType.Error} description={t('sign_in_form.failed_sign_in')} /> : null}

          {recoveryExpired && !isError ? (
            <Alert alertType={AlertType.Info} description={t('sign_in_form.recovery_expired_alert')} />
          ) : null}

          <Button
            onClick={() => {
              void handleAuthStart()
            }}
            className="flex items-center gap-2 py-4 mb-4"
            isLoading={isLoading}
            isDisabled={isFormLoading}
            testId="signIn_primary_btn"
          >
            <SignInIcon color={isFormLoading ? '#575B58' : '#FFFFFF'} />
            {t('sign_in_form.with_passkey')}
          </Button>

          <div className="border-t flex justify-center">
            <span className="text-meta bg-white -mt-3 px-3">or</span>
          </div>

          {isFormError ? <Alert alertType={AlertType.Error} description={t('sign_in_form.failed_sign_in')} /> : null}

          <form onSubmit={(e) => void onSubmit(e)}>
            <div className="mb-4">
              <Controller
                name="email"
                control={control}
                rules={{ required: true, pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i }}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t('sign_in_form.email')}
                    type="email"
                    id="email"
                    testId="email_input"
                    required
                    disabled={isLoading || isFormLoading}
                    error={formErrors.email ? t('sign_in_form.email_error') : undefined}
                  />
                )}
              />
            </div>

            <Button
              type="submit"
              variant={ButtonVariant.Secondary}
              className="w-full py-4"
              isLoading={isFormLoading}
              isDisabled={isLoading}
              testId="signIn_secondary_btn"
            >
              {t('sign_in_form.continue')}
            </Button>

            {isFormError || isError ? (
              <div className="flex justify-end mt-sm">
                <button
                  id="cantSignInCta"
                  type="button"
                  className="flex w-fit-content text-sm text-action items-center gap-2 cursor-pointer font-semibold"
                  onClick={() => {
                    onHelp()
                  }}
                >
                  {t('sign_in_form.cant_sign_in')} <ArrowLongRightIcon className="h-6 w-6" />
                </button>
              </div>
            ) : null}
          </form>
        </div>
      </div>
    </>
  )
}

export const SignInFormCompRedesign: FC<SignInFormProps> = ({ onHelp, recoveryExpired, sessionTimeout = false }) => {
  const [isFormLoading, setIsFormLoading] = useState<boolean>(false)
  const [isError, setIsError] = useState(false)
  const [isFormError, setIsFormError] = useState<PasskeyErrorTypes | AuthsVsPasskeyErrorTypes | null>(null)
  const { t } = useTranslation('features-auth')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectUrl = searchParams.get('redirect')
  const { signInWithPasskey } = useAuth()

  const defaultFormData = useMemo(() => getDefaultFormData(), [])

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors },
  } = useForm({
    defaultValues: defaultFormData,
  })

  const onSubmit = handleSubmit(async (formData: unknown) => {
    sendEvent('user_signIn_click', {
      eventSource: 'egWealth',
      category: 'signIn',
      subCategory: 'intent',
      emailProvided: true,
    })
    try {
      setIsFormLoading(true)
      setIsFormError(null)
      setIsError(false)
      const { email } = formData as FormData
      await riskService.updateFlow('core')
      await signInWithPasskey(email).then(() => {
        log.debug('onSubmit', `navigating to ${redirectUrl ?? WEALTH_ROUTES.OVERVIEW.path}`)
        void navigate(redirectUrl ?? WEALTH_ROUTES.OVERVIEW.path)
      })
    } catch (err) {
      const passkeyError = err as PasskeyError
      const errorType = passkeyError.errorType
      setIsFormError(errorType ?? null)
      log.error('Sign In with Passkey Error: ', err)
    } finally {
      setIsFormLoading(false)
    }
  })

  return (
    <div className="flex flex-col items-center">
      <div className="flex flex-col my-20 md:my-[248px] w-full max-w-[326px] md:max-w-[400px] relative z-10">
        <h1 className="mb-lg md:mb-4xl typo-libre-caslon-text-regular text-[40px] md:text-[56px] text-center">
          <Trans
            i18nKey="sign_in_page.title"
            ns="features-auth"
            components={{
              back: <span className="italic" />,
            }}
          />
        </h1>

        <form onSubmit={(e) => void onSubmit(e)}>
          <Controller
            name="email"
            control={control}
            rules={{ required: true, pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i }}
            render={({ field }) => (
              <Input
                {...field}
                label={t('sign_in_form.email')}
                type="email"
                id="email"
                testId="email_input"
                required
                disabled={isFormLoading}
                error={formErrors.email ? t('sign_in_form.email_error') : undefined}
                inputClassName={cn('pt-6', {
                  'rounded-none rounded-t-md':
                    isFormError || isError || ((recoveryExpired || sessionTimeout) && !isError),
                  'border-red-600': isFormError || isError,
                })}
              />
            )}
          />

          {!isError && sessionTimeout ? (
            <Alert
              alertType={AlertType.Info}
              testId="session-timeout-alert"
              description={t('sign_in_form.session_timeout_message')}
              className="typo-primary-lg rounded-none rounded-b-md border border-sky-200 -mt-px"
            />
          ) : null}

          {recoveryExpired && !isError ? (
            <Alert
              alertType={AlertType.Info}
              description={t('sign_in_form.recovery_expired_alert')}
              className="typo-primary-lg rounded-none rounded-b-md border border-sky-200 -mt-px"
            />
          ) : null}

          {isFormError ? (
            <Alert
              alertType={AlertType.Error}
              description={t(`sign_in_form.error_messages.${isFormError}`) || t('sign_in_form.failed_sign_in')}
              className="typo-primary-lg rounded-none rounded-b-md border border-red-600 -mt-px"
            />
          ) : null}

          {isError ? (
            <Alert
              alertType={AlertType.Error}
              description={t('sign_in_form.failed_sign_in')}
              className="typo-primary-lg rounded-none rounded-b-md border border-red-600 -mt-px"
            />
          ) : null}

          <Button
            type="submit"
            variant={ButtonVariant.Secondary}
            className="w-full py-xs uppercase typo-primary-md-regular mt-2xs"
            isLoading={isFormLoading}
            testId="signIn_secondary_btn"
          >
            {t('sign_in_page.continue')}
          </Button>

          {isFormError || isError ? (
            <div className="flex justify-end mt-2xs">
              <button
                id="cantSignInCta"
                type="button"
                className="flex p-xs w-fit-content typo-primary-md text-text items-center gap-2 cursor-pointer uppercase"
                onClick={onHelp}
              >
                {t('sign_in_form.cant_sign_in')} <ArrowRightIcon className="h-5 w-5 ml-1" />
              </button>
            </div>
          ) : null}
        </form>
      </div>
    </div>
  )
}
